import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQueryClient,
} from 'react-query';
import pDebounce from 'p-debounce';

import { getTokenWithType } from './useAuth';

import { getAccountListForImportEmbedProcedures } from '../utils/urls';

import { linkImportAccountList } from 'types/procedureBuilder';
import { ErrorModal } from 'types/modalTypes';

const pageLength = 20;

async function fetchAccountListForEmbedProcedure({
  keyword,
  start,
  pageLength,
  accountId,
}: TVariables): Promise<TResult> {
  const response = await fetch(
    getAccountListForImportEmbedProcedures(
      accountId,
      start,
      pageLength,
      keyword,
    ),
    {
      method: 'GET',
      headers: {
        Authorization: getTokenWithType(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
  );
  return response.json();
}

const debouncedFetch = pDebounce(fetchAccountListForEmbedProcedure, 500);

type TError = ErrorModal;

type TResult = {
  Data: Array<linkImportAccountList>;
  IsSuccess: boolean;
  StatusCode: number;
};

type TVariables = {
  accountId: number;
  start: number;
  pageLength: number;
  keyword: string;
};

function useGetAccountListForImportEmbedProcedures(
  accountId: number,
  keyword: string,
): UseInfiniteQueryResult<TResult, TError> {
  const queryClient = useQueryClient();
  const data: any | undefined = queryClient.getQueryData([
    'getAccountForImportEmbedProcedures',
    accountId,
    keyword,
  ]);

  const start = data && data.pages ? data.pages.length * pageLength : 0;

  return useInfiniteQuery(
    ['getAccountForImportEmbedProcedures', accountId, keyword],
    () => {
      return debouncedFetch({
        accountId,
        keyword,
        start,
        pageLength,
      });
    },
    {
      getNextPageParam: (lastPage: TResult, allPages: Array<TResult>) => {
        if (lastPage !== null && lastPage?.Data.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TResult, allPages: Array<TResult>) => {
        if (firstPage !== null && firstPage?.Data.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetAccountListForImportEmbedProcedures };
